import { clientApi } from "@/utils/api";
import { TCreateCompanyReqDto, TCreateCompanyResDto } from "@/api/services/admin/company/dtos/create-company.dto";
import { TUpdateCompanyReqDto, TUpdateCompanyResDto } from "@/api/services/admin/company/dtos/update-company.dto";
import { TDeleteCompanyResDto } from "@/api/services/admin/company/dtos/delete-company.dto";
import { ECompanyContractStatus } from "@/types/enum";
import { TGetCompanyListResDto } from "@/api/services/admin/company/dtos/get-company-list.dto";
import { createUrlWithParams } from "@/utils/url";
import { TApiPagReq } from "@/types/api";
import { usePagination } from "@/hooks/usePagination";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useParams } from "react-router-dom";
import { TGetCompanyResDto } from "@/api/services/admin/company/dtos/get-company.dto";
import { isNumber } from "@/utils/number";

export const createCompanyApi = async (payload: TCreateCompanyReqDto): Promise<TCreateCompanyResDto> => {
  const rs = await clientApi.post<TCreateCompanyResDto>("/companies", payload);
  return rs.data;
};

export const updateCompanyApi = async (id: string, payload: TUpdateCompanyReqDto): Promise<TUpdateCompanyResDto> => {
  const rs = await clientApi.put<TUpdateCompanyResDto>(`/companies/${id}`, payload);
  return rs.data;
};

export const deleteCompanyApi = async (id: string): Promise<TDeleteCompanyResDto> => {
  const rs = await clientApi.delete<TDeleteCompanyResDto>(`/companies/${id}`);
  return rs.data;
};

export const fetchCompanyListApi = async (req: TApiPagReq): Promise<TGetCompanyListResDto> => {
  const url = createUrlWithParams("/companies", req);

  const rs = await clientApi.get<TGetCompanyListResDto>(url);
  return rs.data;
};

export const useCompanyList = (contractStatuses?: ECompanyContractStatus[]) => {
  const pagReq = usePagination();
  const params = {
    contractStatuses,
    ...pagReq,
  };
  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST, params],
    queryFn: () => fetchCompanyListApi(params),
  });
};

const getCompanyContractStatusCount = async () => {
  const rs = await clientApi.get("/companies/get-contract-status-count");
  return rs.data;
};

export const useCompanyContractStatusCount = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.GET_CONTRACT_STATUS_COUNT],
    queryFn: () => getCompanyContractStatusCount(),
  });
};

export const fetchCompanyApi = async (companyId: string): Promise<TGetCompanyResDto> => {
  const rs = await clientApi.get<TGetCompanyResDto>(`/companies/${companyId}`);
  return rs.data;
};

export const useCompany = () => {
  const { id } = useParams();

  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL, id],
    queryFn: () => (id ? fetchCompanyApi(id) : undefined),
    enabled: Boolean(id && isNumber(id)),
  });
};
